import { get, post, put } from "aws-amplify/api";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { PacmanLoader } from "react-spinners";
import { createColumnHelper } from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import { createElementIdFactory, createSLMSuburbsElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import "containers/common/List.css";
import "containers/common/Table.css";

const columnHelper = createColumnHelper();
const REQUIRED_FIELDS_MESSAGE = "Suburb, Post Code, State, Sub-Region and Region are all required. The record will NOT be saved until all values have been entered.";

function normalizeSubRegionOptions(subRegionOptions) {
  if (!Array.isArray(subRegionOptions)) {
    return [];
  }

  return subRegionOptions
    .map((option) => {
      const value = option?.value ?? option?.sub_region_id ?? option?.id ?? null;
      const label = option?.label ?? option?.sub_region_name ?? option?.name ?? null;

      if (value === null || label === null) {
        return null;
      }

      const stateCode =
        option?.supplementary_details?.state_code ??
        option?.state_code ??
        null;

      const regionName =
        option?.supplementary_details?.region_name ??
        option?.region_name ??
        null;

      return {
        value: String(value),
        label: String(label),
        stateCode: stateCode != null ? String(stateCode) : null,
        regionName: regionName != null ? String(regionName) : null,
      };
    })
    .filter(Boolean);
}

function isValidPostcode(value) {
  return /^\d{1,4}$/.test(String(value ?? ""));
}

function SLMSuburbList({ isAuthenticated, user, subRegionOptions = [] }) {
  const slmSuburbsIdPrefix = "slm-suburbs-list";
  const tempIdRef = useRef(0);

  const [isLoading, setIsLoading] = useState(true);
  const [ui, setUi] = useState({ showAlert: false, errorMessage: "" });
  const [sorting, setSorting] = useState([]);
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({ suburb: "", postcode: "" });

  const createId = useMemo(
    () => createElementIdFactory(slmSuburbsIdPrefix),
    [slmSuburbsIdPrefix]
  );

  const tableElementIds = useMemo(
    () => createSLMSuburbsElementIds(createId),
    [createId]
  );

  const normalizedSubRegions = useMemo(
    () => normalizeSubRegionOptions(subRegionOptions),
    [subRegionOptions]
  );

  const fetchSuburbs = useCallback(async (filter = { suburb: "", state: "" }) => {
    setIsLoading(true);

    try {
      const response = await get({
        apiName: "colin",
        path: "/slmsuburbs",
        options: {
          queryParams: filter,
        },
      }).response;

      const json = await response.body.json();
      const withClientIds = (json || []).map((record) => ({
        ...record,
        _clientId: record?._clientId ?? `existing-${record.suburb_id}`,
      }));

      setData(withClientIds);
      setUi({ showAlert: false, errorMessage: "" });
      document.title = "Suburbs lookup";
    } catch (error) {
      console.error("Failed to fetch SLM suburbs", error);
      setUi({ showAlert: true, errorMessage: "Failed to load suburbs." });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    fetchSuburbs({ suburb: "", state: "" });
  }, [isAuthenticated, fetchSuburbs]);

  const getRecordKey = useCallback((record) => {
    if (record?.suburb_id !== undefined && record?.suburb_id !== null) {
      return `id-${record.suburb_id}`;
    }
    return `tmp-${record?._clientId}`;
  }, []);

  const updateRecordByIndex = useCallback(
    (rowIndex, updateFn) => {
      const rowRecord = data[rowIndex];
      if (!rowRecord) {
        return;
      }

      const targetKey = getRecordKey(rowRecord);

      setData((previous) =>
        previous.map((record) => {
          if (getRecordKey(record) !== targetKey) {
            return record;
          }

          return typeof updateFn === "function" ? updateFn(record) : record;
        })
      );
    },
    [data, getRecordKey]
  );

  const updateTableState = useCallback(
    (rowIndex, columnId, value) => {
      updateRecordByIndex(rowIndex, (record) => {
        const next = {
          ...record,
          [columnId]: value,
          modified: true,
        };

        if (columnId === "sub_region_id") {
          const selected = normalizedSubRegions.find(
            (option) => String(option.value) === String(value)
          );

          if (selected) {
            next.sub_region_name = selected.label;
            next.state_code = selected.stateCode;
            next.region_name = selected.regionName;
          } else {
            next.sub_region_name = null;
            next.state_code = null;
            next.region_name = null;
          }
        }

        return next;
      });
    },
    [normalizedSubRegions, updateRecordByIndex]
  );

  const saveRecord = useCallback(
    async (record) => {
      const isNew = Boolean(record.new_record || !record.suburb_id);
      const path = isNew ? "/slmsuburbs/new" : `/slmsuburbs/${record.suburb_id}`;
      const method = isNew ? post : put;

      const response = await method({
        apiName: "colin",
        path,
        options: {
          body: {
            ...record,
            last_edited_by: user,
          },
        },
      }).response;

      return response.body.json();
    },
    [user]
  );

  const validateRecord = useCallback((record) => {
    const hasRequiredFields =
      Boolean((record?.name || "").trim()) &&
      Boolean(String(record?.sub_region_id || "").trim()) &&
      Boolean(String(record?.state_code || "").trim()) &&
      Boolean(String(record?.region_name || "").trim()) &&
      isValidPostcode(record?.postcode);

    if (!hasRequiredFields) {
      return REQUIRED_FIELDS_MESSAGE;
    }

    return null;
  }, []);

  const handleSave = useCallback(
    async ({ row, column, value }) => {
      const updatedRecord = {
        ...row.original,
        [column.id]: value,
      };

      if (column.id === "sub_region_id") {
        const selected = normalizedSubRegions.find(
          (option) => String(option.value) === String(value)
        );

        if (selected) {
          updatedRecord.sub_region_name = selected.label;
          updatedRecord.state_code = selected.stateCode;
          updatedRecord.region_name = selected.regionName;
        } else {
          updatedRecord.sub_region_name = null;
          updatedRecord.state_code = null;
          updatedRecord.region_name = null;
        }
      }

      const validationMessage = validateRecord(updatedRecord);
      if (validationMessage) {
        updateRecordByIndex(row.index, (record) => ({
          ...record,
          ...updatedRecord,
          modified: true,
          isSaving: false,
        }));
        setUi({ showAlert: true, errorMessage: validationMessage });
        return { skipLocalUpdate: true };
      }

      updateRecordByIndex(row.index, (record) => ({ ...record, isSaving: true }));

      try {
        const result = await saveRecord(updatedRecord);

        if (!result?.result) {
          setUi({
            showAlert: true,
            errorMessage: result?.error_message || "Failed to save suburb.",
          });
          updateRecordByIndex(row.index, (record) => ({ ...record, isSaving: false }));
          return false;
        }

        updateRecordByIndex(row.index, (record) => ({
          ...record,
          ...updatedRecord,
          suburb_id: result.id ?? record.suburb_id,
          modified: false,
          new_record: false,
          isSaving: false,
        }));

        setUi({ showAlert: false, errorMessage: "" });
        return true;
      } catch (error) {
        console.error("Failed to save SLM suburb", error);
        setUi({ showAlert: true, errorMessage: "Network error: failed to save suburb." });
        updateRecordByIndex(row.index, (record) => ({ ...record, isSaving: false }));
        return false;
      }
    },
    [normalizedSubRegions, saveRecord, updateRecordByIndex, validateRecord]
  );

  const addRecord = useCallback(() => {
    const nextTempId = tempIdRef.current;
    tempIdRef.current += 1;

    setData((previous) => [
      {
        _clientId: `new-${nextTempId}`,
        suburb_id: null,
        name: "",
        postcode: "",
        sub_region_id: "",
        sub_region_name: "",
        region_name: "",
        state_code: "",
        created_by: user,
        last_edited_by: user,
        new_record: true,
        modified: true,
      },
      ...previous,
    ]);
  }, [user]);

  const handleFilterChange = useCallback((event) => {
    const { id, name, value } = event.target;
    const field = name || id;

    if (field === "postcode") {
      const numericOnly = value.replace(/\D/g, "").slice(0, 4);
      setFilters((previous) => ({ ...previous, postcode: numericOnly }));
      return;
    }

    setFilters((previous) => ({ ...previous, [field]: value }));
  }, []);

  const handleSearch = useCallback(
    async (event) => {
      event.preventDefault();

      await fetchSuburbs({
        suburb: filters.suburb || "",
        state: filters.postcode || "",
      });
    },
    [fetchSuburbs, filters]
  );

  const columns = useMemo(() => {
    const subRegionCell = (props) => {
      if (normalizedSubRegions.length > 0) {
        return <EditableCell {...props} options={normalizedSubRegions} />;
      }
      return <EditableCell {...props} />;
    };

    return [
      columnHelper.accessor("suburb_id", {
        id: "suburb_id",
        header: "ID",
        cell: (info) => info.getValue() ?? "",
        size: 90,
      }),
      columnHelper.accessor("name", {
        id: "name",
        header: "Suburb",
        cell: EditableCell,
        size: 240,
      }),
      columnHelper.accessor("postcode", {
        id: "postcode",
        header: "Postcode",
        cell: EditableCell,
        size: 120,
        meta: {
          numericOnly: true,
          maxLength: 4,
        },
      }),
      columnHelper.accessor("sub_region_id", {
        id: "sub_region_id",
        header: "Sub-Region",
        cell: subRegionCell,
        sortingFn: (rowA, rowB, columnId) => {
          const getSubRegionLabel = (row) => {
            const rowLabel = row.original?.sub_region_name;
            if (rowLabel) {
              return String(rowLabel);
            }

            const option = normalizedSubRegions.find(
              (item) => String(item.value) === String(row.getValue(columnId) ?? "")
            );
            return option?.label ?? "";
          };

          return getSubRegionLabel(rowA).localeCompare(getSubRegionLabel(rowB));
        },
        size: 220,
      }),
      columnHelper.accessor("region_name", {
        id: "region_name",
        header: "Region",
        cell: (info) => info.getValue() ?? "",
        size: 160,
      }),
      columnHelper.accessor("state_code", {
        id: "state_code",
        header: "State",
        cell: (info) => info.getValue() ?? "",
        size: 110,
      }),
    ];
  }, [normalizedSubRegions]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="mt-3">
      <Alert
        show={ui.showAlert}
        variant="danger"
        onClose={() => setUi((previous) => ({ ...previous, showAlert: false }))}
        dismissible
      >
        {ui.errorMessage}
      </Alert>

      <Form onSubmit={handleSearch}>
        <Row>
          <Col md={3}>
            <Form.Group controlId="suburb">
              <Form.Control
                id={tableElementIds.suburbFilter}
                name="suburb"
                type="text"
                value={filters.suburb}
                onChange={handleFilterChange}
                placeholder="Suburb containing"
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group controlId="postcode">
              <Form.Control
                id={tableElementIds.postcodeFilter}
                name="postcode"
                type="text"
                inputMode="numeric"
                value={filters.postcode}
                onChange={handleFilterChange}
                placeholder="Postcode equals"
                maxLength={4}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Button id={tableElementIds.searchButton} type="submit" variant="secondary">
              Search
            </Button>
          </Col>
        </Row>
      </Form>

      <Row>
        <Col className="text-right" style={{ marginBottom: "12px" }}>
          <Button id={tableElementIds.addButton} variant="secondary" size="sm" onClick={addRecord}>
            Add
          </Button>
        </Col>
      </Row>

      <Row>
        <Col>
          {isLoading ? (
            <div className="outer-div">
              <div className="sweet-loading inner-div">
                <PacmanLoader
                  cssOverride={{ display: "block", margin: "0 auto" }}
                  size={20}
                  color={"#e03c31"}
                  loading={isLoading}
                />
              </div>
            </div>
          ) : (
            <div className="custom-data-table">
              <GenericTable
                columns={columns}
                data={data}
                section="slm-suburbs-list"
                elementIds={tableElementIds}
                pageSize={PAGE_SIZES.slmLookupSuburbs}
                enablePagination={true}
                state={{ sorting }}
                onSortingChange={setSorting}
                enableSortingRemoval={false}
                getRowId={(row) => String(row.suburb_id ?? row._clientId)}
                meta={{
                  updateData: updateTableState,
                  handleSave,
                }}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default SLMSuburbList;